/* ============================================================
   artvision® · KÜNSTLERN FOLGEN
   Firestore: collectors/{uid}/followed_artists/{shopifyCollectionId}
   mit { collection_id, consent_version, created_at, last_seen_at } —
   collection_id ist die Dokument-ID (für die Follower-Abfrage des
   Backends), consent_version die Version des Einwilligungshinweises
   (Folgen = Einwilligung in E-Mails), die Zeiten serverTimestamp();
   genau das, was firebase/firestore.rules im Dashboard-Repo erlaubt.
   Das Dashboard zeigt die gefolgten Künstler unter „Gefolgte
   Künstler“ und zählt dort „N neue Werke“ seit last_seen_at.
   Mechanik (Live-Zustand, Login, Resume-after-Auth):
   assets/av-collector-toggle.js.

   Auf der Künstlerseite trägt der Schalter data-av-follow-seen:
   Folgt der Collector dieser Person, gilt der Besuch als „gesehen“
   und last_seen_at wird einmal pro Seitenaufruf aktualisiert —
   sonst stünde im Dashboard weiter „N neue Werke“, obwohl die
   Werke gerade angeschaut wurden.
   ============================================================ */

import { startCollectorToggle } from 'av-collector-toggle';
import {
  FOLLOWED_ARTISTS_COLLECTION,
  FOLLOW_FIELDS,
  FOLLOW_ID_FIELD,
  FOLLOW_TIMESTAMP_FIELDS,
  LAST_SEEN_FIELD,
  createFollowAction,
  normalizeShopifyId
} from 'av-collector-core';

const SELECTOR = '[data-av-follow-toggle]';
const ID_ATTRIBUTE = 'data-collection-id';

/** Artist ids already marked as seen on this page view (per collector). */
const markedAsSeen = new Set();

function artistIdsOnThisPage() {
  const ids = Array.from(document.querySelectorAll(`${SELECTOR}[data-av-follow-seen]`))
    .map((button) => normalizeShopifyId(button.getAttribute(ID_ATTRIBUTE)))
    .filter(Boolean);
  return Array.from(new Set(ids));
}

/** Follows created right now already carry last_seen_at = now — no second write needed. */
function idsJustCreated(snapshot) {
  return new Set(
    snapshot.docs.filter((entry) => entry.metadata && entry.metadata.hasPendingWrites).map((entry) => entry.id)
  );
}

function markArtistsAsSeen({ user, ids, snapshot, firestoreApi, reference }) {
  const justCreated = idsJustCreated(snapshot);

  artistIdsOnThisPage()
    .filter((id) => ids.has(id) && !markedAsSeen.has(`${user.uid}:${id}`))
    .forEach((id) => {
      markedAsSeen.add(`${user.uid}:${id}`);
      if (justCreated.has(id)) return;

      firestoreApi
        .updateDoc(firestoreApi.doc(reference, id), { [LAST_SEEN_FIELD]: firestoreApi.serverTimestamp() })
        .catch((error) => {
          // Only the "new works" marker in the dashboard stays; the next visit tries again.
          console.error('Artist could not be marked as seen', { id, error });
        });
    });
}

startCollectorToggle({
  name: 'Follow',
  selector: SELECTOR,
  idAttribute: ID_ATTRIBUTE,
  collection: FOLLOWED_ARTISTS_COLLECTION,
  timestampFields: FOLLOW_TIMESTAMP_FIELDS,
  idField: FOLLOW_ID_FIELD,
  fields: FOLLOW_FIELDS,
  actionType: 'follow',
  createAction: createFollowAction,
  readActionId: (action) => action.collectionId,
  onIdsChange: markArtistsAsSeen
});
